import { ApiError, api } from '@alpha/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { PageHeader } from '../../components/PageHeader';
import { formatDateTime, formatLds } from '../../lib/format';
import { Alert, Button, Card, Chip, FormGroup, Input, Spinner, StatCard, StatCards, Table, TableWrap, Td, Th } from '../../ui';
import { WITHDRAWAL_TABS } from './WithdrawalList';

const SETTINGS_KEY = ['auto-withdrawal-settings'];
const REPORT_KEY = ['auto-withdrawal-report'];

/**
 * 2026-08-02 automatic on-chain LDS withdrawal — client requirement: as soon
 * as a member withdraws, LDS is sent automatically from a hot wallet the
 * client controls. This page is the only place that wallet's private key is
 * ever entered. The key field below is WRITE-ONLY by design: on load it is
 * always blank (the API never returns key material — see
 * HotWalletKeyStore's docblock on the API side), and after saving, the field
 * is cleared and only the derived wallet address is shown back, so the admin
 * can confirm they typed the right key without the key itself ever round-
 * tripping through the browser again.
 */
export function AutoWithdrawal() {
  const queryClient = useQueryClient();
  const [keyInput, setKeyInput] = useState('');
  const [keySavedAddress, setKeySavedAddress] = useState<string | null>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => api.withdrawals.autoWithdrawal.settings().then((r) => r.data),
  });

  const { data: report } = useQuery({
    queryKey: REPORT_KEY,
    queryFn: () => api.withdrawals.autoWithdrawal.report().then((r) => r.data),
  });

  const settingsForm = useForm<{
    enabled: boolean;
    max_per_tx_lds: string;
    max_per_day_lds: string;
    gas_limit_units: string;
  }>({
    values: settings
      ? {
          enabled: settings.enabled,
          max_per_tx_lds: settings.max_per_tx_lds,
          max_per_day_lds: settings.max_per_day_lds,
          gas_limit_units: String(settings.gas_limit_units),
        }
      : undefined,
  });

  const saveSettings = useMutation({
    mutationFn: (values: { enabled: boolean; max_per_tx_lds: string; max_per_day_lds: string; gas_limit_units: string }) =>
      api.withdrawals.autoWithdrawal.updateSettings({
        enabled: values.enabled,
        max_per_tx_lds: values.max_per_tx_lds,
        max_per_day_lds: values.max_per_day_lds,
        gas_limit_units: Number(values.gas_limit_units),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });

  const saveKey = useMutation({
    mutationFn: (privateKey: string) => api.withdrawals.autoWithdrawal.setWalletKey(privateKey),
    onSuccess: (res) => {
      setKeyInput('');
      setKeySavedAddress(res.data.wallet_address);
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });

  const clearKey = useMutation({
    mutationFn: () => api.withdrawals.autoWithdrawal.clearWalletKey(),
    onSuccess: () => {
      setKeySavedAddress(null);
      queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });

  const retryPayout = useMutation({
    mutationFn: (id: string) => api.withdrawals.autoWithdrawal.retryPayout(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REPORT_KEY });
      queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
    },
  });

  if (isLoading) return <Spinner />;

  return (
    <>
      <PageHeader
        title="Auto Withdrawal"
        description="Automatic on-chain LDS payout — hot wallet, kill switch, and caps"
        tabs={WITHDRAWAL_TABS}
      />

      <Card>
        <h3 className="card-title">Kill switch &amp; safety caps</h3>
        {saveSettings.isSuccess ? <Alert tone="success">Settings saved.</Alert> : null}
        {saveSettings.isError ? (
          <Alert tone="danger">{(saveSettings.error as ApiError)?.message ?? 'Failed to save settings.'}</Alert>
        ) : null}

        <form
          onSubmit={settingsForm.handleSubmit((values) => saveSettings.mutate(values))}
          noValidate
        >
          <FormGroup label="Auto withdrawal enabled">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" {...settingsForm.register('enabled')} />
              <span>
                {settingsForm.watch('enabled')
                  ? 'ON — new withdrawals within caps are paid automatically.'
                  : 'OFF (kill switch) — every withdrawal waits for manual approval.'}
              </span>
            </label>
          </FormGroup>

          <FormGroup label="Max per transaction (LDS)">
            <Input
              type="number"
              step="any"
              min="0"
              {...settingsForm.register('max_per_tx_lds', { required: true })}
            />
          </FormGroup>

          <FormGroup label="Max per day, total across all members (LDS)">
            <Input
              type="number"
              step="any"
              min="0"
              {...settingsForm.register('max_per_day_lds', { required: true })}
            />
          </FormGroup>

          <FormGroup label="Gas limit (units)">
            <Input type="number" step="1" min="21000" max="1000000" {...settingsForm.register('gas_limit_units', { required: true })} />
          </FormGroup>

          <Button type="submit" variant="gold" disabled={saveSettings.isPending}>
            {saveSettings.isPending ? 'Saving…' : 'Save Settings'}
          </Button>
        </form>
        <p className="hint">
          A withdrawal above either cap, or requested while the switch is off, is left for manual
          approval exactly as before — nothing about the existing approval flow changes.
        </p>
      </Card>

      <Card>
        <h3 className="card-title">Hot wallet</h3>

        {settings?.has_key ? (
          <>
            <p>
              <strong>Address:</strong> {settings.wallet_address}
            </p>
            <p>
              <strong>Key set:</strong> {formatDateTime(settings.key_set_at)}
            </p>
            <StatCards>
              <StatCard
                label="BNB balance (gas)"
                value={settings.hot_wallet_bnb_balance ? `${settings.hot_wallet_bnb_balance} BNB` : '—'}
              />
              <StatCard
                label="LDS balance"
                value={settings.hot_wallet_lds_balance ? formatLds(settings.hot_wallet_lds_balance) : '—'}
              />
            </StatCards>
            <Button
              variant="danger"
              onClick={() => {
                if (window.confirm('Clear the hot wallet key? Auto-withdrawal will stop signing anything until a new key is set.')) {
                  clearKey.mutate();
                }
              }}
              disabled={clearKey.isPending}
            >
              {clearKey.isPending ? 'Clearing…' : 'Clear key'}
            </Button>
          </>
        ) : (
          <Alert tone="warning">No hot wallet key configured — auto-withdrawal cannot send anything yet.</Alert>
        )}

        {keySavedAddress ? (
          <Alert tone="success">
            Key saved. Derived address: {keySavedAddress}. Verify this matches the wallet you intended
            before enabling the kill switch above.
          </Alert>
        ) : null}
        {saveKey.isError ? (
          <Alert tone="danger">{(saveKey.error as ApiError)?.message ?? 'Failed to save key.'}</Alert>
        ) : null}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (keyInput.trim()) saveKey.mutate(keyInput.trim());
          }}
          noValidate
        >
          <FormGroup label={settings?.has_key ? 'Replace private key' : 'Private key'}>
            <Input
              type="password"
              autoComplete="off"
              placeholder="64 hex characters, 0x prefix optional"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
            />
          </FormGroup>
          <p className="hint">
            This field never shows the currently-saved key — the server stores it encrypted and never
            returns it again, to anyone, for any reason. Only the wallet address it derives to is ever
            shown back to you.
          </p>
          <Button type="submit" variant="gold" disabled={saveKey.isPending || !keyInput.trim()}>
            {saveKey.isPending ? 'Saving…' : 'Save key'}
          </Button>
        </form>
      </Card>

      <Card>
        <h3 className="card-title">Report</h3>
        {report ? (
          <>
            <StatCards>
              <StatCard label="Auto-paid (total)" value={String(report.auto_paid_count)} />
              <StatCard label="Auto-paid LDS (total)" value={formatLds(report.auto_paid_total_lds)} />
              <StatCard label="Auto-paid LDS (today)" value={formatLds(report.auto_paid_today_lds)} />
              <StatCard label="Manual pending" value={String(report.manual_pending_count)} />
              <StatCard label="Failed sends" value={String(report.failed_count)} />
            </StatCards>

            {report.failed.length > 0 ? (
              <>
                <h4>Failed auto-payouts — need a manual decision</h4>
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <Th>User</Th>
                        <Th>Amount</Th>
                        <Th>Reason</Th>
                        <Th>When</Th>
                        <Th>Action</Th>
                      </tr>
                    </thead>
                    <tbody>
                    {report.failed.map((row) => (
                      <tr key={row.id}>
                        <Td>{row.user_code ?? '—'}</Td>
                        <Td>{formatLds(row.amount_lds)}</Td>
                        <Td wrap>{row.reject_reason ?? '—'}</Td>
                        <Td>{formatDateTime(row.processed_at)}</Td>
                        <Td>
                          <Chip
                            as="button"
                            tone="gold"
                            onClick={() => retryPayout.mutate(row.id)}
                          >
                            {retryPayout.isPending && retryPayout.variables === row.id ? 'Retrying…' : 'Retry'}
                          </Chip>
                        </Td>
                      </tr>
                    ))}
                    </tbody>
                  </Table>
                </TableWrap>
              </>
            ) : null}
          </>
        ) : (
          <Spinner />
        )}
      </Card>
    </>
  );
}
